import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  Container, Box, Typography, Paper, Button, Alert, 
  CircularProgress, Snackbar 
} from '@mui/material';
import { Email, Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const EmailVerificationPending = () => {
  const location = useLocation();
  const { resendVerificationEmail, authLoading } = useAuth();
  
  // Get email from location state (passed from registration or login)
  const email = location.state?.email || '';
  const emailSent = location.state?.emailSent;
  const warning = location.state?.warning;
  const fromLogin = location.state?.fromLogin || false;
  
  const [resendStatus, setResendStatus] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  const handleResendEmail = async () => {
    if (!email) {
      setResendStatus({ success: false, message: 'Email address not found' });
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const result = await resendVerificationEmail(email);
      setResendStatus(result);
      setSnackbarOpen(true);
    } catch (error) {
      setResendStatus({ 
        success: false, 
        message: 'Failed to resend verification email' 
      });
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Email sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              {fromLogin ? 'Email Verification Required' : 'Check Your Email'}
            </Typography>
          </Box>
          
          {fromLogin ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" color="text.secondary" paragraph>
                We found your account, but your email address needs to be verified before you can log in.
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {emailSent ? 
                  "We've just sent a new verification email to:" :
                  "Please check your email for a verification link sent to:"
                }
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" paragraph>
              We've sent a verification link to:
            </Typography>
          )}
          
          <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
            {email}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            Please click the link in the email to verify your account and complete your {fromLogin ? 'login' : 'registration'}.
          </Typography>
          
          {warning && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {warning}
            </Alert>
          )}
          
          {!emailSent && !fromLogin && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Verification email could not be sent. Please try resending it.
            </Alert>
          )}
          
          {emailSent && (
            <Alert severity="success" sx={{ mb: 3 }}>
              ✅ A verification email has been sent! Please check your inbox and spam folder.
            </Alert>
          )}
          
          <Box sx={{ mt: 4, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Didn't receive the email?
            </Typography>
            
            <Button
              variant="outlined"
              startIcon={authLoading ? <CircularProgress size={20} /> : <Refresh />}
              onClick={handleResendEmail}
              disabled={authLoading}
              sx={{ mt: 1 }}
            >
              {authLoading ? 'Sending...' : 'Resend Verification Email'}
            </Button>
          </Box>
          
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Having trouble? Here are some tips:
            </Typography>
            <Box sx={{ textAlign: 'left', display: 'inline-block', mb: 3 }}>
              <Typography variant="body2" color="text.secondary" component="li">
                Check your spam/junk folder
              </Typography>
              <Typography variant="body2" color="text.secondary" component="li">
                Make sure the email address is correct
              </Typography>
              <Typography variant="body2" color="text.secondary" component="li">
                Wait a few minutes for the email to arrive
              </Typography>
              <Typography variant="body2" color="text.secondary" component="li">
                Try resending the verification email
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Already verified your email?{' '}
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Typography component="span" variant="body2" color="primary">
                  Sign In
                </Typography>
              </Link>
            </Typography>
          </Box>
        </Paper>
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={resendStatus?.success ? 'success' : 'error'}
            sx={{ width: '100%' }}
          >
            {resendStatus?.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default EmailVerificationPending; 